import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Sidebar from './components/Sidebar';
import ChatContainer from './components/ChatContainer';
import Login from './components/Login';
import AuthService from './services/authService';
import { TenantProvider } from './contexts/TenantContext';
import { ProjectProvider } from './contexts/ProjectContext';

interface Thread {
  id: string;
  title: string;
  timestamp?: Date | string;
  // Tenant/Project context
  tenantId?: string;
  tenantName?: string;
  projectUuid?: string;
  projectName?: string;
}

interface ThreadContext {
  tenantId?: string;
  tenantName?: string;
  projectUuid?: string;
  projectName?: string;
}

interface EnvironmentInfo {
  customer_name: string;
  environment_url: string;
  is_development: boolean;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [forceLogin, setForceLogin] = useState<boolean>(false);
  const authService = AuthService.getInstance();

  // Store threadId in localStorage to persist across page refreshes
  const [threadId, setThreadId] = useState<string>(() => {
    return localStorage.getItem('nomnomChatThreadId') || '';
  });

  const [threadTitle, setThreadTitle] = useState<string>('');

  // Fetch environment info and update page title
  useEffect(() => {
    const fetchEnvironmentInfo = async () => {
      try {
        const baseUrl = (import.meta as any).env.VITE_API_URL || "";
        const response = await fetch(`${baseUrl}/environment`);
        if (response.ok) {
          const data: EnvironmentInfo = await response.json();
          // Update the page title with environment name
          document.title = `Nomi - ${data.customer_name}`;
        }
      } catch (error) {
        console.error("Failed to fetch environment info:", error);
      }
    };

    fetchEnvironmentInfo();
  }, []);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);

      // If forceLogin is true, skip auto-authentication
      if (forceLogin) {
        authService.logout();
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      const authenticated = authService.isAuthenticated();

      if (authenticated) {
        // Validate token with backend to ensure it's still valid
        const isValid = await authService.validateToken();
        if (!isValid) {
          // Token is invalid, clear it
          authService.logout();
        }
        setIsAuthenticated(isValid);
      } else {
        setIsAuthenticated(false);
      }

      setIsLoading(false);
    };

    checkAuth();
  }, [authService, forceLogin]);

  // Auto-restore most recent thread when user logs in
  useEffect(() => {
    // Only run when authenticated and loading is complete
    if (!isAuthenticated || isLoading) return;

    // Check if there are saved threads to restore
    const savedThreads = localStorage.getItem('chatThreads');
    if (savedThreads) {
      try {
        const threads: Thread[] = JSON.parse(savedThreads);
        if (threads.length > 0) {
          // First, check if we have a previously selected thread
          const lastThreadId = localStorage.getItem('nomnomChatThreadId');
          const lastThread = lastThreadId ? threads.find(t => t.id === lastThreadId) : null;

          // Use last selected thread if it exists, otherwise use most recent
          let targetThread: Thread;
          if (lastThread) {
            targetThread = lastThread;
          } else {
            // Sort by timestamp and select most recent
            const sorted = threads.sort((a, b) => {
              const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
              const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
              return bTime - aTime;
            });
            targetThread = sorted[0];
          }

          setThreadId(targetThread.id);
          setThreadTitle(targetThread.title || '');
        }
      } catch (error) {
        console.error('Failed to parse saved threads:', error);
      }
    }
  }, [isAuthenticated, isLoading]);

  // Save threadId to localStorage whenever it changes
  useEffect(() => {
    if (threadId) {
      localStorage.setItem('nomnomChatThreadId', threadId);
    }
  }, [threadId]);

  // Create a new thread
  const createNewThread = () => {
    setThreadId('');
    setThreadTitle('');
    localStorage.removeItem('nomnomChatThreadId');
  };

  // Switch to an existing thread
  const switchToThread = (newThreadId: string) => {
    setThreadId(newThreadId);
    // Load thread title from localStorage
    const savedThreads = localStorage.getItem('chatThreads');
    if (savedThreads) {
      const threads = JSON.parse(savedThreads);
      const thread = threads.find((t: Thread) => t.id === newThreadId);
      setThreadTitle(thread?.title || '');
    }
  };

  // Update thread title (and optionally tenant/project context)
  const updateThreadTitle = (threadIdToUpdate: string, title: string, context?: ThreadContext) => {
    setThreadTitle(title);

    // Update localStorage - handle race condition where thread might not exist yet
    const savedThreads = localStorage.getItem('chatThreads');
    let threads: Thread[] = savedThreads ? JSON.parse(savedThreads) : [];

    const existingIndex = threads.findIndex((t: Thread) => t.id === threadIdToUpdate);

    if (existingIndex >= 0) {
      // Thread exists - update it (preserve existing context if not provided)
      threads[existingIndex] = {
        ...threads[existingIndex],
        title,
        ...(context && {
          tenantId: context.tenantId,
          tenantName: context.tenantName,
          projectUuid: context.projectUuid,
          projectName: context.projectName,
        })
      };
    } else {
      // Thread doesn't exist yet (race condition) - create it with the title and context
      threads = [{
        id: threadIdToUpdate,
        title,
        timestamp: new Date().toISOString(),
        ...(context && {
          tenantId: context.tenantId,
          tenantName: context.tenantName,
          projectUuid: context.projectUuid,
          projectName: context.projectName,
        })
      }, ...threads];
    }

    localStorage.setItem('chatThreads', JSON.stringify(threads));

    // Dispatch custom event for same-window listeners
    // (The 'storage' event only fires for cross-tab changes)
    window.dispatchEvent(new Event('chatThreadsUpdated'));
  };

  // Handle login
  const handleLogin = async (username: string, password: string): Promise<boolean> => {
    // Clear any existing authentication state before attempting login
    authService.logout();
    setIsAuthenticated(false);

    const success = await authService.login(username, password);
    if (success) {
      setIsAuthenticated(true);
      setForceLogin(false); // Reset force login flag after successful login
    }
    return success;
  };

  // Handle logout
  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setForceLogin(true);
    setThreadId('');
    setThreadTitle('');
  };

  // Handle forcing a new login (clears session and shows login page)
  const handleForceLogin = () => {
    authService.logout();
    setIsAuthenticated(false);
    setForceLogin(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Router basename={import.meta.env.BASE_URL.replace(/\/+$/, '')}>
      <div className="app">
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated && !forceLogin ? (
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} onClearSession={handleForceLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <TenantProvider>
                  <ProjectProvider>
                    <Sidebar
                      isOpen={true}
                      threadId={threadId}
                      onNewThread={createNewThread}
                      onSwitchThread={switchToThread}
                      onUpdateThreadTitle={updateThreadTitle}
                      onLogout={handleLogout}
                    />
                    <ChatContainer
                      threadId={threadId}
                      onThreadCreated={setThreadId}
                      threadTitle={threadTitle}
                      onUpdateThreadTitle={updateThreadTitle}
                    />
                  </ProjectProvider>
                </TenantProvider>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
